import config from '../configLoader';
import {RoleInfo, RoleWriteInput} from '../interfaces/EmployeeInfoInterface';
import {EmployeeInfo} from '../interfaces/EmployeeInfoInterface';
import client from './client';
import { SiteConfig } from '../interfaces/ConfigInterface';

// Function to get employees based on pay period ID, type of timesheet, and supervisor ID
export const getEmployees = async () => {
    try {

      // Make the API request to fetch the employees' data based on the constructed endpoint and parameters
      const response = await client.get("/admin/employees");

      // Log the response data to the console
      console.log("Employees:", response.data);

      // Return the response object, which contains the employees' data
      return response;

    } catch (error) {
      // Log any errors that occur during the fetch process
      console.error("Error fetching employees:", error);

      // Throw an error to indicate that fetching employees failed
      throw new Error("Failed to fetch employees");
    }
};

export const getAdminEmployees = async () => {
  try {

    // Make the API request to fetch the employees' data based on the constructed endpoint and parameters
    const response = await client.get("/admin/adminEmployees");

    // Log the response data to the console
    console.log("Admin Roles:", response.data);

    // Return the response object, which contains the employees' data
    return response;

  } catch (error) {
    // Log any errors that occur during the fetch process
    console.error("Error fetching admin employees:", error);

    // Throw an error to indicate that fetching employees failed
    throw new Error("Failed to fetch admin employees");
  }
};

export const getEmployeeRoles = async () => {
  try {

    // Make the API request to fetch the employees' data based on the constructed endpoint and parameters
    const response = await client.get("/admin/roles");

    // Log the response data to the console
    console.log("Roles:", response.data);

    // Return the response object, which contains the employees' data
    return response;

  } catch (error) {
    // Log any errors that occur during the fetch process
    console.error("Error fetching roles:", error);

    // Throw an error to indicate that fetching employees failed
    throw new Error("Failed to fetch roles");
  }
};

export const getSupervisors = async () => {
  try {
    // Make the API request to fetch supervisors
    const response = await client.get("/admin/supervisors");

    // Log the response data to the console
    console.log("Supervisors:", response.data);

    // Return the response object, which contains the supervisors' data
    return response;

  } catch (error) {
    // Log any errors that occur during the fetch process
    console.error("Error fetching supervisors:", error);

    // Throw an error to indicate that fetching supervisors failed
    throw new Error("Failed to fetch supervisors");
  }
};

/**
 * The API's role write responses are 200 {success:false, errorMessage} on a DB error, so
 * a 200 can still be a failure. Turn that into a thrown Error (axios errors, e.g. 400
 * ROLE_HAS_EMPLOYEES or 403, are rethrown untouched so callers can read status/code).
 */
const throwIfRoleWriteFailed = (data: any, fallback: string) => {
  if (data && data.success === false) {
    throw new Error(typeof data.errorMessage === 'string' && data.errorMessage ? data.errorMessage : fallback);
  }
};

/** PUT /admin/roles/:id (ManageEmployeeRoles). `active` must always be sent: the API
 *  writes Active from it. Writes only the flag keys present in the payload. */
export const editEmployeeRole = async (roleID: number, updatedRole: RoleWriteInput): Promise<void> => {
  const response = await client.put(`/admin/roles/${roleID}`, updatedRole);
  throwIfRoleWriteFailed(response.data, 'Failed to update role');
};

/** POST /admin/roles (ManageEmployeeRoles). Unsent flags are written as 0, so send all of
 *  them (allowMobileApp in particular). Returns the created role. */
export const addEmployeeRole = async (newRole: RoleWriteInput): Promise<RoleInfo | undefined> => {
  const response = await client.post("/admin/roles", newRole);
  throwIfRoleWriteFailed(response.data, 'Failed to add role');
  return response.data?.role;
};

export const syncEmployeeData = async (altEmployeeID: String, altLocationID: String) => {
  try {
    // Make the API request to sync employee data with the altEmployeeID and altLocationID in the request body
    const response = await client.post(config.employeeSyncAPIUrl, {
      altEmployeeID: altEmployeeID,
      altCompanyID: altLocationID
    });

    const success = response.data.success;
    const errMessage = response.data.errorMessage;

    // Log the response data to the console
    success === true 
      ? console.log("Sync successful:", response.data) 
      : console.log("Sync Failed:", errMessage);

    // Return the response object
    return response.data;
  } catch (error) {
    // Log any errors that occur during the sync process
    console.error("Error during sync:", error);

    // Throw an error to indicate that syncing failed
    throw new Error("Failed to sync employee.");
  }
};

export const updateEmployeeAssignedRole = async (employeeID: number, roleID: number) => {
  try {
    const response = await client.put(`/admin/employees/${employeeID}/role`, { roleID });
    return response.data;
  } catch (error) {
    console.error("Error updating employee role assignment:", error);
    throw error;
  }
};

export const updateEmployeeCanAddDriveTime = async (employeeID: number, canAddDriveTime: boolean) => {
  try {
    const response = await client.put(`/admin/employees/${employeeID}/canAddDriveTime`, { canAddDriveTime });
    return response.data;
  } catch (error) {
    console.error("Error updating employee drive-time setting:", error);
    throw error;
  }
};

/** PUT /admin/employees/:id/companyPhone — 403 without ManageCompanyPhones.
 *  startDate (YYYY-MM-DD) is required when hasCompanyPhone is true. */
export const updateEmployeeCompanyPhone = async (employeeID: number, hasCompanyPhone: boolean, startDate?: string) => {
  try {
    const response = await client.put(`/admin/employees/${employeeID}/companyPhone`, { hasCompanyPhone, startDate });
    return response.data;
  } catch (error) {
    console.error("Error updating employee company phone setting:", error);
    throw error;
  }
};

/** PUT /admin/employees/:id/active — manually activate / deactivate an employee.
 *  403 when the caller lacks ActivateInactivateEmployees; 400 for self-deactivation. */
export const updateEmployeeActive = async (employeeID: number, active: boolean) => {
  try {
    const response = await client.put(`/admin/employees/${employeeID}/active`, { active });
    return response.data;
  } catch (error) {
    console.error("Error updating employee active status:", error);
    throw error;
  }
};

export const updateEmployeeCanCreateWorkOrders = async (employeeID: number, canCreateWorkOrders: boolean) => {
  try {
    const response = await client.put(`/admin/employees/${employeeID}/canCreateWorkOrders`, { canCreateWorkOrders });
    return response.data;
  } catch (error) {
    console.error("Error updating employee work-order-creation setting:", error);
    throw error;
  }
};
